import React, { useState } from "react";
import BackButton from "../components/backButton";
import { TopAdminNav } from "../layouts/TopAdminNav";
import { RegisterCard } from "../components/RegisterCard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBook, faSearch } from "@fortawesome/free-solid-svg-icons";
import { Menu, MenuButton, MenuItem } from "@szhsin/react-menu";
import {
  useAssignSubjectToClassMutation,
  useDeleteSubjectMutation,
  useGetClassroomsQuery,
  useGetSubjectQuery,
} from "../hooks/useAdmin";
import loader from "../assets/loader.gif";
import moment from "moment";
import Swal from "sweetalert2";
import CustomModal from "../components/CustomModal/CustomModal";
import { SubjectForm } from "../layouts/editForms/SubjectForm";
import { CircleLoader } from "../components/CircleLoader";
import SmallModal from "../components/SmallModal";

export interface ISubject {
  id: string;
  school_id: string;
  name: string;
  code: string;
  description: string;
  created_at: string;
  updated_at: string;
}

const RegisterSubject = () => {
  const { data, isLoading } = useGetSubjectQuery();
  const deleteSub = useDeleteSubjectMutation();
  const [openModal, setOpenModal] = useState(false);
  const [singleSubject, setSingleSubject] = useState<ISubject | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [openAssignModal, setOpenAssignModal] = useState(false);
  const { data: classrooms } = useGetClassroomsQuery();
  const [classRoomId, setClassRoomId] = useState("");
  const assign = useAssignSubjectToClassMutation({
    close: () => setOpenAssignModal(false),
  });

  async function deleteSubject(subjectId: string) {
    Swal.fire({
      icon: "warning",
      title: "Delete Subject",
      text: "Are you sure you want to delete this subject",
      showCancelButton: true,
      confirmButtonColor: "#f43f5e",
      confirmButtonText: "Yes, Delete",
      cancelButtonColor: "#64748b",
      cancelButtonText: "No, Cancel",
    }).then((response) => {
      if (response.isConfirmed) {
        deleteSub.mutate({
          subjectId,
        });
      }
    });
  }

  const handleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleAssign = () => {
    assign.mutate({
      subject_ids: selectedIds,
      classroomId: classRoomId || classrooms?.data[0]?.id,
    });
    setSelectedIds([]);
    setClassRoomId("");
  };

  return (
    <div className="w-full">
      <div className="w-full flex items-center mb-4">
        <BackButton />
        <TopAdminNav name="Jane Doe" title="Subject Management" />
      </div>

      <div className="flex w-full">
        <div className="block w-full h-[87vh] overflow-auto register-scroll">
          <div className="flex px-1 mb-5">
            <div className="w-full">
              <RegisterCard
                title="Subject Configuration Matrix"
                details="Configure, map, and manage all academic curriculum course subjects"
              />
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5 max-w-[98%] mx-auto">
            {/* Header Toolbar Utility Section */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
              {/* Search Element Layout Wrapper */}
              <div className="w-full max-w-xs relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                  <FontAwesomeIcon icon={faSearch} className="text-xs" />
                </div>
                <input
                  id="searchStaffInput"
                  placeholder="Search curriculum subjects..."
                  className="w-full rounded-xl pl-9 pr-4 py-2 text-xs font-medium text-slate-700 bg-slate-50/50 border border-slate-200 placeholder-slate-400 focus:outline-none focus:border-[#0096D1] focus:bg-white transition-all"
                  type="text"
                />
              </div>

              {/* Functional Dynamic Group Actions */}
              <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
                {selectedIds.length > 0 && (
                  <button
                    onClick={() => setOpenAssignModal(true)}
                    className="inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-bold shadow-sm hover:bg-emerald-100 active:scale-95 transition-all"
                  >
                    <FontAwesomeIcon icon={faBook} className="text-[10px]" />
                    Assign Selected ({selectedIds.length})
                  </button>
                )}

                <button
                  onClick={() => {
                    setSingleSubject(null);
                    setOpenModal(true);
                  }}
                  className="inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 bg-[#0096D1] text-white text-xs font-bold shadow-sm hover:bg-[#007ba8] active:scale-95 transition-all"
                >
                  <FontAwesomeIcon icon={faBook} className="text-[10px]" />
                  Add Subject
                </button>
              </div>
            </div>

            {/* Structured Table Container Layer */}
            <div className="w-full overflow-x-auto rounded-xl border border-slate-50">
              <table className="w-full border-collapse text-left table-fixed min-w-[700px]">
                <thead>
                  <tr className="bg-[#F4F7FE] border-b border-slate-100 text-[11px] font-bold tracking-wider text-[#012063] uppercase">
                    <th className="py-3.5 px-4 w-[40%] text-left">
                      Subject Parameters
                    </th>
                    <th className="py-3.5 px-4 w-[25%] text-center">
                      Subject Code
                    </th>
                    <th className="py-3.5 px-4 w-[20%] text-center">
                      Date Created
                    </th>
                    <th className="py-3.5 px-4 w-[15%] text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {isLoading ? (
                    <tr>
                      <td colSpan={4} className="py-12">
                        <div className="flex justify-center items-center">
                          <img
                            src={loader}
                            alt="loader"
                            className="w-10 h-10 object-contain"
                          />
                        </div>
                      </td>
                    </tr>
                  ) : data?.data?.length > 0 ? (
                    data.data.map((item: any, index: number) => (
                      <tr
                        key={index}
                        className="group bg-white hover:bg-slate-50/40 transition-colors duration-150 text-[13px] text-slate-600 font-medium"
                      >
                        {/* 1. Subject Checkbox and Name Identification Parameter */}
                        <td className="py-3.5 px-4">
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-slate-300 text-[#0096D1] focus:ring-[#0096D1] accent-[#0096D1] cursor-pointer"
                              checked={selectedIds.includes(item.id)}
                              onChange={() => handleSelect(item.id)}
                            />
                            <span className="font-bold text-slate-800 tracking-tight capitalize group-hover:text-[#0096D1] transition-colors">
                              {item.name ?? "—"}
                            </span>
                          </div>
                        </td>

                        {/* 2. Alpha-numeric Code Block */}
                        <td className="py-3.5 px-4 text-center whitespace-nowrap">
                          <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded bg-slate-100 text-slate-600 font-mono text-xs uppercase font-bold tracking-wider border border-slate-200/40">
                            {item.code ? item.code.slice(0, 12) : "—"}
                          </span>
                        </td>

                        {/* 3. Created Temporal Timestamp */}
                        <td className="py-3.5 px-4 text-center text-xs text-slate-400 font-medium whitespace-nowrap">
                          {moment(item?.created_at).format("MM/DD/YY")}
                        </td>

                        {/* 4. Action Layer Configurations */}
                        <td className="py-3.5 px-4 text-center whitespace-nowrap align-middle">
                          <Menu
                            menuButton={
                              <MenuButton className="inline-flex items-center justify-center gap-1 rounded-xl px-3 py-1.5 bg-slate-50 border border-slate-200 text-slate-700 text-xs font-bold shadow-sm hover:bg-slate-100 active:scale-95 transition-all">
                                Options
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  strokeWidth={2.5}
                                  stroke="currentColor"
                                  className="w-2.5 h-2.5 text-slate-400"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                                  />
                                </svg>
                              </MenuButton>
                            }
                            transition
                            align="end"
                            offsetY={4}
                            menuClassName="bg-white border border-slate-100 rounded-xl shadow-xl p-1.5 min-w-[150px]"
                          >
                            <MenuItem
                              onClick={() => {
                                setSingleSubject(item);
                                setOpenModal(true);
                              }}
                              className="flex items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-600 rounded-lg cursor-pointer hover:bg-sky-50 hover:text-[#0096D1] transition-colors"
                            >
                              Edit Subject
                            </MenuItem>
                            <div className="my-1 border-t border-slate-50" />
                            <MenuItem
                              onClick={() => deleteSubject(item?.id)}
                              className="flex items-center gap-2 px-3 py-2 text-xs font-semibold text-rose-600 rounded-lg cursor-pointer hover:bg-rose-50 transition-colors"
                            >
                              Delete Subject
                            </MenuItem>
                          </Menu>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={4}
                        className="py-8 text-center text-slate-400 text-sm"
                      >
                        No configured curriculum subjects discovered...
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Primary Subject Editor Sheet Modal Overlay */}
      {openModal && (
        <CustomModal
          title={
            singleSubject
              ? "Edit Subject Configuration"
              : "Create Curriculum Subject"
          }
          width={60}
          hideModal={() => setOpenModal(false)}
        >
          <SubjectForm
            singleSubject={singleSubject}
            close={() => setOpenModal(false)}
          />
        </CustomModal>
      )}

      {/* Classroom Mapping Dynamic Assignment Utility Sheet */}
      {openAssignModal && (
        <SmallModal
          title={
            selectedIds.length > 1
              ? "Assign Subjects to Classrooms"
              : "Assign Subject to Classroom"
          }
          isOpen={openAssignModal}
          onClose={() => setOpenAssignModal(false)}
        >
          <div className="flex flex-col items-center gap-5 p-2 text-left">
            <div className="w-full flex flex-col gap-1.5">
              <label className="text-xs font-bold text-slate-700">
                Target Classroom Destination
              </label>
              <select
                onChange={(e) => setClassRoomId(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#0096D1] focus:ring-1 focus:ring-[#0096D1] transition-all bg-slate-50/50 appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%2364748b%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:1.25rem_1.25rem] bg-[right_0.875rem_center] bg-no-repeat pr-10"
                defaultValue={classrooms?.data[0]?.id || ""}
              >
                <option value="">All Classrooms</option>
                {classrooms?.data?.map((clas: any, idx: number) => (
                  <option key={idx} value={clas.id}>
                    {clas.name}
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={handleAssign}
              disabled={assign?.isPending}
              type="button"
              className="w-full inline-flex items-center justify-center rounded-xl px-5 py-2.5 bg-[#012063] hover:bg-[#0096D1] disabled:bg-slate-400 text-white font-bold text-sm shadow-md transition-all transform active:scale-95 gap-2 mt-2"
            >
              {assign?.isPending ? (
                <>
                  <CircleLoader />
                  <span>Processing...</span>
                </>
              ) : (
                "Confirm Mapping"
              )}
            </button>
          </div>
        </SmallModal>
      )}
    </div>
  );
};

export default RegisterSubject;
